// The garden seen through the hall's patio door. The hall background has transparent glass
// (scripts/home_door_glass.py); the view sits UNDER it at depth -1, and whatever sticks out
// beyond the panes is hidden by the opaque wall. A glass layer (tint + highlight streaks) sits
// between the two so it reads as "through a window", not as a hole.
//
// What you see is the painted MAX-LEVEL garden at the current time of day — the prettiest art in
// the game, kept on as the view even though the garden itself is retired — with the farm's real
// crops standing in its beds, at whatever stage they have actually reached.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { GLASS_STREAKS, streakPolygon } from './glassEffect.js';
import { gameState } from '../state/gameState.js';
import { currentPhase } from '../state/dayCycle.js';
import { plotStage, cropStageKey, cropArtId } from '../farm/farmRules.mjs';
import {
  GARDEN_BEDS, GARDEN_W, GARDEN_H, WINDOW_SCALE, WINDOW_VIEW,
  gardenBgKey, bedBox, windowTransform, fitWindowView, bedPlantings, cropTintForPhase,
} from '../render/gardenWindow.mjs';
import { serverNow } from '../state/serverClock.mjs';

export { WINDOW_SCALE };

// Shown only if the garden art has not landed yet — a flat dusk sky, so a pane mid-download
// reads as a window on a dim day rather than a hole in the wall.
const SKY_FALLBACK = 0x9fb8cf;

// Glass effect: a milky tint over the whole pane plus soft diagonal highlights, drawn
// between the farm view (depth -1) and the hall background (0). The frame around the
// panes is opaque, so the layer needs no mask — only the cut glass lets it through.
export const GLASS_TINT = { color: 0xe1eeff, alpha: 0.22 };
const GLASS_DEPTH = -0.5;

const rectCache = new Map();

// Bounding box (world px) of the texture's non-opaque pixels = the door glass.
export function glassRectForTexture(scene, key) {
  if (rectCache.has(key)) return rectCache.get(key);
  let rect = null;
  const tex = scene.textures.get(key);
  const src = tex?.getSourceImage?.();
  if (src && src.width) {
    try {
      const cv = document.createElement('canvas');
      cv.width = src.width; cv.height = src.height;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('2d context unavailable');
      ctx.drawImage(src, 0, 0);
      const { data } = ctx.getImageData(0, 0, cv.width, cv.height);
      let x0 = Infinity, y0 = Infinity, x1 = -1, y1 = -1;
      for (let y = 0; y < cv.height; y++) {
        for (let x = 0; x < cv.width; x++) {
          if (data[(y * cv.width + x) * 4 + 3] < 128) {
            if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
          }
        }
      }
      if (x1 >= 0) {
        const fx = GAME_WIDTH / cv.width, fy = GAME_HEIGHT / cv.height;
        rect = { x: x0 * fx, y: y0 * fy, w: (x1 - x0 + 1) * fx, h: (y1 - y0 + 1) * fy };
      }
    } catch (e) {
      console.warn('[FarmWindow] glass scan failed for', key, e);
      rect = null;
    }
  }
  rectCache.set(key, rect);
  return rect;
}

// Canvas texture with the highlight streaks for a pane of this size (cached by size).
// Each streak is a parallelogram filled with a transparent→white→transparent gradient
// across its width — soft edges without `ctx.filter` (not available on older tablets).
function streakTextureKey(scene, rect) {
  const w = Math.ceil(rect.w), h = Math.ceil(rect.h);
  const key = `glass_streaks_${w}x${h}`;
  if (scene.textures.exists(key)) return key;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return null;
  const ctx = tex.getContext();
  const local = { x: 0, y: 0, w, h };
  for (const s of GLASS_STREAKS) {
    const [bl, br, tr, tl] = streakPolygon(local, s);
    // Gradient runs across the streak at mid-height (perpendicular enough for a soft look).
    const midL = { x: (bl.x + tl.x) / 2, y: h / 2 }, midR = { x: (br.x + tr.x) / 2, y: h / 2 };
    const g = ctx.createLinearGradient(midL.x, midL.y, midR.x, midR.y);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.5, `rgba(255,255,255,${s.alpha})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(bl.x, bl.y); ctx.lineTo(br.x, br.y); ctx.lineTo(tr.x, tr.y); ctx.lineTo(tl.x, tl.y);
    ctx.closePath(); ctx.fill();
  }
  tex.refresh();
  return key;
}

function attachGlassEffect(scene, rect) {
  const objs = [];
  objs.push(scene.add.rectangle(rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w, rect.h,
    GLASS_TINT.color, GLASS_TINT.alpha).setDepth(GLASS_DEPTH));
  try {
    const key = streakTextureKey(scene, rect);
    if (key) objs.push(scene.add.image(rect.x, rect.y, key).setOrigin(0, 0).setDepth(GLASS_DEPTH));
  } catch (e) {
    console.warn('[FarmWindow] glass streaks unavailable', e);   // tint alone still reads as glass
  }
  return { destroy: () => { for (const o of objs) o.destroy(); objs.length = 0; } };
}

// How tall a crop stands relative to its bed's width. The art is drawn to fill a farm plot,
// which is the same idea as a garden bed, so its own aspect ratio is the right one to keep.
function plantAt(scene, container, bed, plot, stage, tint) {
  const colourKey = `farm_crop_${cropArtId(plot.seed, plot.c)}_${stage}`;      // a flower colour, else the plain crop
  const key = scene.textures.exists(colourKey) ? colourKey : `farm_crop_${plot.seed}_${stage}`;
  if (!scene.textures.exists(key)) return null;
  const box = bedBox(bed);
  const src = scene.textures.get(key).getSourceImage();
  const w = box.w, h = w * (src.height / src.width);
  // Bottom-centre on the bed's front edge, a little inside it — a plant grows at the front of
  // its bed, the same rule the farm's plots use.
  const img = scene.add.image(box.cx, box.front - 4, key).setOrigin(0.5, 1).setDisplaySize(w, h).setTint(tint);
  container.add(img);
  return img;
}

export function attachGardenWindow(scene, { homeBgKey, getData }) {
  const rect = glassRectForTexture(scene, homeBgKey);
  if (!rect) return { destroy() {} };     // texture without cut glass → no window

  // Everything the window draws lives in one container, scaled and slid so that the chosen
  // garden point lands on the pane's top-left corner.
  const container = scene.add.container(0, 0).setDepth(-1);
  // Behind everything: the pane must never be a hole onto the scene's own background, and on the
  // first frame after a phase rolls over the new background may not have downloaded yet.
  const backdrop = scene.add.rectangle(rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w, rect.h,
    SKY_FALLBACK, 1).setDepth(-2);
  const glass = attachGlassEffect(scene, rect);
  let bgImg = null;
  const plants = [];

  const apply = () => {
    const data = getData?.() ?? null;
    // Background: the max-level garden at the current phase. Falls back to whatever phase art
    // did load, then to nothing (the pane then shows the plain glass tint rather than a hole).
    const phase = currentPhase();
    const wanted = gardenBgKey(phase);
    const key = scene.textures.exists(wanted) ? wanted : null;
    if (key && (!bgImg || bgImg.texture.key !== key)) {
      bgImg?.destroy();
      bgImg = scene.add.image(0, 0, key).setOrigin(0, 0).setDisplaySize(GARDEN_W, GARDEN_H);
      container.addAt(bgImg, 0);
    }
    // Crops: the first twenty planted cells, each in its own bed at its real stage.
    for (const p of plants) p.destroy();
    plants.length = 0;
    const now = serverNow();
    bedPlantings(data?.farm).forEach(({ plot }, i) => {
      const stage = cropStageKey(plot, plotStage(plot, now), now);
      const img = plantAt(scene, container, GARDEN_BEDS[i], plot, stage, cropTintForPhase(phase));
      if (img) plants.push(img);
    });
    const view = fitWindowView(rect, WINDOW_VIEW, WINDOW_SCALE);
    const t = windowTransform(rect, view, view.scale);
    container.setPosition(t.x, t.y).setScale(t.scale);
  };

  apply();
  const unsub = gameState.on(apply);
  const tick = scene.time.addEvent({ delay: 1000, loop: true, callback: apply });
  let alive = true;
  const destroy = () => {
    if (!alive) return;
    alive = false;
    unsub?.(); tick.remove(); glass.destroy(); backdrop.destroy();
    for (const p of plants) p.destroy();
    bgImg?.destroy(); container.destroy();
  };
  scene.events.once('shutdown', destroy);
  return { destroy, rect };
}
